import { gql } from "@apollo/client";
import type { SyncableSymptom } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Symptom as RxSymptom } from "../collections/symptom";
import type { RxDBCollections } from "../provider";
import {
  SyncSymptomPullDocument,
  type WholeSyncableSymptomFragment,
} from "./__generated__/symptom.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateSymptom = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxSymptom>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncSymptomPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<RxSymptom, SyncableSymptom, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlSymptomToRxSymptom,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "symptom",
  });
};

export const mapGqlSymptomToRxSymptom = (
  symptom: WholeSyncableSymptomFragment,
): WithDeleted<RxSymptom> => ({
  id: symptom.id,
  name: symptom.name,
  externalCoding: {
    icd10: symptom.externalCoding?.icd10
      ? {
          code: symptom.externalCoding.icd10.code,
          system: symptom.externalCoding.icd10.system ?? null,
        }
      : null,
    icd11: symptom.externalCoding?.icd11
      ? {
          code: symptom.externalCoding.icd11.code,
          system: symptom.externalCoding.icd11.system ?? null,
        }
      : null,
    snomed: symptom.externalCoding?.snomed
      ? {
          code: symptom.externalCoding.snomed.code,
          system: symptom.externalCoding.snomed.system ?? null,
        }
      : null,
    loinc: symptom.externalCoding?.loinc
      ? {
          code: symptom.externalCoding.loinc.code,
          system: symptom.externalCoding.loinc.system ?? null,
        }
      : null,
  },
  i18n: symptom.i18n.map((i18n) => ({
    locale: i18n.locale,
    displayName: i18n.displayName,
  })),
  meta: {
    createdAt: symptom.meta.createdAt,
    createdByUserId: symptom.meta.createdByUserId,
    updatedAt: symptom.meta.updatedAt,
    updatedByUserId: symptom.meta.updatedByUserId,
  },
  _deleted: symptom.meta.deleted,
});

gql`
  fragment WholeSyncableSymptom on SyncableSymptom {
    id
    name
    externalCoding {
      icd10 {
        code
        system
      }
      icd11 {
        code
        system
      }
      snomed {
        code
        system
      }
      loinc {
        code
        system
      }
    }
    i18n {
      locale
      displayName
    }
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncSymptomPull($input: PullSymptomInput!) {
    pullSymptom(input: $input) {
      documents {
        ...WholeSyncableSymptom
      }
    }
  }
`;
